import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { getVisitorId } from '@/lib/visitor';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import SEOHead from '@/components/marketplace/SEOHead';
import { SponsoredAd } from '@/components/marketplace/SponsoredAd';
import { loadAdSense } from '@/lib/adsense';
import { resolveEffectiveOgImage, absoluteUrl } from '@/lib/ogImage';

const ADSENSE_CLIENT = process.env.REACT_APP_ADSENSE_CLIENT;
const ADSENSE_INARTICLE_SLOT = process.env.REACT_APP_ADSENSE_SLOT_INARTICLE;
const ADSENSE_INARTICLE_ON = !!(ADSENSE_CLIENT && ADSENSE_INARTICLE_SLOT);
import {
  Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink, BreadcrumbSeparator, BreadcrumbPage,
} from '@/components/ui/breadcrumb';
import { Clock, Tag, ChevronLeft, ListOrdered, PlusCircle, Sparkles, User } from 'lucide-react';


const useSlugify = (text) => useMemo(() => {
  return (text || '').toString().toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');
}, [text]);


/**
 * Parses the HTML content to:
 *  - Add id attributes to every h2/h3 so the table of contents can link to them
 *  - Inject a single sponsored inline ad AFTER the SECOND h2 (or after ~40%
 *    of the content if fewer than 2 h2 headings). Returns the mutated HTML
 *    string + a TOC array.
 */
const useProcessedContent = (html, insertMarker) => {
  return useMemo(() => {
    if (!html) return { html: '', toc: [] };
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const headings = Array.from(doc.body.querySelectorAll('h2, h3'));
    const toc = [];
    headings.forEach((h, i) => {
      const raw = h.textContent || '';
      const slug = raw.toLowerCase().replace(/[^a-z0-9\s-]/g, '').trim().replace(/\s+/g, '-') + '-' + i;
      h.id = slug;
      toc.push({ id: slug, text: raw, level: h.tagName.toLowerCase() });
    });

    // Determine anchor for inline ad placement:
    // Prefer AFTER the 2nd h2. Otherwise place at midpoint of body children.
    if (insertMarker) {
      const h2s = headings.filter(h => h.tagName === 'H2');
      let anchor = null;
      if (h2s.length >= 2) anchor = h2s[1];
      else {
        const kids = Array.from(doc.body.children);
        if (kids.length > 3) anchor = kids[Math.floor(kids.length / 2)];
      }
      if (anchor) {
        const marker = doc.createElement('div');
        marker.setAttribute('data-indus-ad-marker', '1');
        anchor.parentNode.insertBefore(marker, anchor.nextSibling);
      }
    }

    return { html: doc.body.innerHTML, toc };
  }, [html, insertMarker]);
};


const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [related, setRelated] = useState([]);
  const [popularCats, setPopularCats] = useState([]);
  const [inlineAd, setInlineAd] = useState(null);
  const contentRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/blog/posts/${slug}`),
      api.get('/sponsored-ads/public', { params: { placement: 'blog_inline', limit: 1 } }).catch(() => ({ data: [] })),
      api.get('/blog/categories').catch(() => ({ data: [] })),
    ]).then(async ([p, ad, cats]) => {
      setPost(p.data);
      setInlineAd((ad.data || [])[0] || null);
      setPopularCats((cats.data || []).slice(0, 8));
      if (p.data.category_id) {
        const rr = await api.get('/blog/posts', { params: { category_id: p.data.category_id, limit: 4 } }).catch(() => ({ data: { items: [] } }));
        setRelated((rr.data.items || []).filter(x => x.id !== p.data.id).slice(0, 3));
      }
    }).finally(() => setLoading(false));
  }, [slug]);

  const processed = useProcessedContent(post?.html_content, !!inlineAd || ADSENSE_INARTICLE_ON);

  // After content is rendered, fill the inline-ad marker: house ad first,
  // else backfill with a Google AdSense in-article unit (if configured).
  useEffect(() => {
    if (!contentRef.current) return;
    const marker = contentRef.current.querySelector('[data-indus-ad-marker]');
    if (!marker || marker.getAttribute('data-indus-ad-filled')) return;

    if (inlineAd) {
      marker.setAttribute('data-indus-ad-filled', '1');
      // Fire impression once
      api.post(`/sponsored-ads/${inlineAd.id}/impression`, null, { params: { vid: getVisitorId() } }).catch(() => {});
      // Render a static, styled anchor
    marker.innerHTML = `
      <a href="${inlineAd.click_url || '#'}"
         data-testid="blog-inline-sponsored"
         class="not-prose block my-8 rounded-2xl overflow-hidden border shadow-sm bg-background hover:shadow-md transition-shadow ring-1 ring-primary/10 group"
         ${/^https?:\/\//i.test(inlineAd.click_url) ? 'target="_blank" rel="noreferrer sponsored"' : ''}>
        <div class="relative aspect-[21/6] bg-secondary">
          <img src="${inlineAd.image_url}" alt="${(inlineAd.title || '').replace(/"/g, '&quot;')}" class="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
          <div class="absolute inset-0 bg-gradient-to-r from-black/60 via-black/20 to-transparent"></div>
          <div class="absolute inset-0 p-5 sm:p-6 flex flex-col justify-center max-w-2xl">
            <div class="text-white text-lg sm:text-2xl font-semibold leading-snug">${inlineAd.title}</div>
            ${inlineAd.subtitle ? `<div class="text-white/85 text-sm mt-1">${inlineAd.subtitle}</div>` : ''}
            ${inlineAd.advertiser_name ? `<div class="text-white/70 text-[11px] mt-2">Sponsored · ${inlineAd.advertiser_name}</div>` : ''}
          </div>
          <span class="absolute top-2 right-2 text-[10px] font-medium uppercase rounded bg-black/60 text-white px-1.5 py-0.5">Sponsored</span>
        </div>
      </a>`;
    // Wire click tracking
    const anchor = marker.querySelector('a');
    if (anchor) {
      anchor.addEventListener('click', () => {
        api.post(`/sponsored-ads/${inlineAd.id}/click`, null, { params: { vid: getVisitorId() } }).catch(() => {});
      });
    }
    } else if (ADSENSE_INARTICLE_ON) {
      // Backfill: Google AdSense in-article unit
      marker.setAttribute('data-indus-ad-filled', '1');
      marker.innerHTML = `<ins class="adsbygoogle not-prose block my-8" style="display:block;text-align:center" data-ad-layout="in-article" data-ad-format="fluid" data-ad-client="${ADSENSE_CLIENT}" data-ad-slot="${ADSENSE_INARTICLE_SLOT}"></ins>`;
      loadAdSense(ADSENSE_CLIENT)
        .then(() => { (window.adsbygoogle = window.adsbygoogle || []).push({}); })
        .catch((e) => console.warn('AdSense in-article not initialized:', e?.message || e));
    }
  }, [processed.html, inlineAd]);

  if (loading) return <div className='indus-container py-16 text-center text-muted-foreground'>Loading article…</div>;
  if (!post) return <div className='indus-container py-16 text-center'>Post not found. <Link to='/blog' className='text-primary hover:underline'>Back to blog</Link></div>;

  return (
    <div className='indus-container py-8'>
      <SEOHead
        title={`${post.meta_title || post.title} · IndusFact Blog`}
        description={post.meta_description || post.excerpt}
        keywords={[post.focus_keyword, ...(post.tags || [])].filter(Boolean).join(', ')}
        image={absoluteUrl(resolveEffectiveOgImage(post, 'blog'))}
        schemaEndpoint={`/seo/blog/${post.slug}`}
      />

      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem><BreadcrumbLink asChild><Link to='/'>Home</Link></BreadcrumbLink></BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem><BreadcrumbLink asChild><Link to='/blog'>Blog</Link></BreadcrumbLink></BreadcrumbItem>
          {post.category_name && (<><BreadcrumbSeparator /><BreadcrumbItem><BreadcrumbLink asChild><Link to={`/blog/category/${post.category_slug || post.category_id}`}>{post.category_name}</Link></BreadcrumbLink></BreadcrumbItem></>)}
          <BreadcrumbSeparator />
          {/* #5: Long titles get proper CSS truncation with the full title
              available on hover (title attribute) — no more mid-word cut-off. */}
          <BreadcrumbItem className='min-w-0'>
            <BreadcrumbPage
              className='block max-w-[16rem] sm:max-w-sm md:max-w-md lg:max-w-lg overflow-hidden text-ellipsis whitespace-nowrap'
              title={post.title}
              data-testid='blog-breadcrumb-title'
            >
              {post.title}
            </BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className='mt-6 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-8 lg:gap-10'>
        {/* Main column */}
        <article data-testid='blog-post-article'>
          {post.category_name && <div className='text-xs uppercase tracking-wider text-accent'>{post.category_name}</div>}
          <h1 className='text-3xl sm:text-4xl font-semibold tracking-tight mt-2'>{post.title}</h1>
          <div className='mt-3 flex items-center gap-3 text-xs text-muted-foreground flex-wrap' data-testid='blog-top-byline'>
            {post.author_name && (
              <span className='inline-flex items-center gap-1'>
                <User className='h-3 w-3' /> By <span className='font-medium text-foreground'>{post.author_name}</span>
              </span>
            )}
            <span className='flex items-center gap-1'><Clock className='h-3 w-3' /> {new Date(post.published_at || post.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
            {post.view_count > 0 && <span>· {post.view_count} views</span>}
            {post.tags?.length > 0 && (
              // #6: Tags are clickable now — each opens the blog index
              // filtered by that tag. Requires the /blog?tag=... support in
              // Blog.jsx.
              <span className='flex items-center gap-1 flex-wrap'>
                <Tag className='h-3 w-3' />
                {post.tags.map((t, i) => (
                  <Link
                    key={t}
                    to={`/blog?tag=${encodeURIComponent(t)}`}
                    className='inline-flex items-center rounded-full border px-2 py-0.5 hover:bg-secondary hover:text-foreground transition-colors'
                    data-testid={`blog-tag-${i}`}
                  >
                    {t}
                  </Link>
                ))}
              </span>
            )}
          </div>
          {post.cover_image_url && (
            <div className='mt-6 rounded-xl overflow-hidden border aspect-[16/9] bg-muted'>
              <img src={post.cover_image_url} alt={post.title} className='w-full h-full object-cover' />
            </div>
          )}
          <div
            ref={contentRef}
            className='prose prose-neutral max-w-none mt-6 text-[15px] leading-relaxed indus-prose'
            dangerouslySetInnerHTML={{ __html: processed.html }}
            data-testid='blog-post-content'
          />

          {/* Full author card — bottom-of-article placement (per Review.pdf #2) */}
          {post.author_name && (
            <div className='mt-10 flex items-start gap-4 rounded-xl border bg-secondary/40 p-5' data-testid='blog-author-block'>
              {post.author_avatar_url ? (
                <img src={post.author_avatar_url} alt={post.author_name} className='h-14 w-14 rounded-full object-cover border shrink-0' />
              ) : (
                <div className='h-14 w-14 rounded-full bg-primary/10 text-primary flex items-center justify-center text-base font-semibold border shrink-0'>
                  {post.author_name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}
                </div>
              )}
              <div className='min-w-0'>
                <div className='text-[11px] uppercase tracking-wider text-muted-foreground font-medium'>Written by</div>
                <div className='text-base font-semibold mt-0.5' data-testid='blog-author-name'>{post.author_name}</div>
                {post.author_bio && <div className='text-sm text-muted-foreground mt-1.5 leading-relaxed'>{post.author_bio}</div>}
              </div>
            </div>
          )}

          <div className='mt-10 border-t pt-6 flex items-center justify-between'>
            <Link to='/blog' className='text-sm text-primary hover:underline inline-flex items-center'>
              <ChevronLeft className='h-4 w-4 mr-1' /> Back to blog
            </Link>
          </div>

          {related.length > 0 && (
            <section className='mt-12'>
              <h2 className='text-xl font-semibold tracking-tight'>Related articles</h2>
              <div className='mt-4 grid md:grid-cols-3 gap-4'>
                {related.map(r => (
                  <Link to={`/blog/${r.slug}`} key={r.id}>
                    <Card className='p-5 h-full hover:shadow-md transition-shadow'>
                      <div className='text-[11px] uppercase tracking-wider text-accent'>{r.category_name}</div>
                      <h3 className='font-semibold mt-1 leading-snug'>{r.title}</h3>
                      {r.excerpt && <p className='text-sm text-muted-foreground mt-2 line-clamp-2'>{r.excerpt}</p>}
                    </Card>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>

        {/* Sidebar */}
        <aside className='space-y-4 lg:sticky lg:top-20 self-start' data-testid='blog-sidebar'>
          {/* TOC */}
          {processed.toc.length > 1 && (
            <Card className='p-4' data-testid='blog-toc-card'>
              <div className='text-xs uppercase tracking-wider text-muted-foreground font-medium flex items-center gap-1 mb-2'>
                <ListOrdered className='h-3.5 w-3.5' /> On this page
              </div>
              <ul className='space-y-1.5 text-sm'>
                {processed.toc.map(t => (
                  <li key={t.id} className={t.level === 'h3' ? 'ml-3' : ''}>
                    <a
                      href={`#${t.id}`}
                      className='text-muted-foreground hover:text-foreground hover:underline underline-offset-4'
                      onClick={(e) => {
                        e.preventDefault();
                        const el = document.getElementById(t.id);
                        if (el) {
                          const y = el.getBoundingClientRect().top + window.scrollY - 80;
                          window.scrollTo({ top: y, behavior: 'smooth' });
                        }
                      }}
                    >
                      {t.text}
                    </a>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {/* Post a listing CTA */}
          <Card className='p-5 bg-primary text-primary-foreground' data-testid='blog-cta-card'>
            <div className='flex items-center gap-2 mb-1.5'>
              <Sparkles className='h-4 w-4' />
              <div className='text-xs uppercase tracking-wider font-medium'>Featured</div>
            </div>
            <div className='font-semibold text-lg leading-tight'>List your industrial products on IndusFact</div>
            <p className='text-sm text-primary-foreground/80 mt-1.5'>Reach verified buyers. Post free listings — Sell, Wanted, or Rent.</p>
            <Link to='/post' className='mt-4 inline-block'>
              <Button size='sm' className='bg-accent text-accent-foreground hover:bg-accent/90'>
                <PlusCircle className='h-4 w-4 mr-1.5' /> Post a listing
              </Button>
            </Link>
          </Card>

          {/* Popular categories */}
          {popularCats.length > 0 && (
            <Card className='p-4' data-testid='blog-popular-cats-card'>
              <div className='text-xs uppercase tracking-wider text-muted-foreground font-medium mb-2'>Popular categories</div>
              <div className='flex flex-wrap gap-1.5'>
                {popularCats.map(c => (
                  <Link
                    key={c.id}
                    to={`/blog/category/${c.slug || c.id}`}
                    className='rounded-full border bg-background px-2.5 py-1 text-[11px] hover:bg-secondary'
                    data-testid={`blog-popular-cat-${c.slug || c.id}`}
                  >
                    {c.name}{c.post_count ? ` · ${c.post_count}` : ''}
                  </Link>
                ))}
              </div>
            </Card>
          )}

          {/* Sponsored side card */}
          <div data-testid='blog-side-sponsored'>
            <SidebarSponsored />
          </div>
        </aside>
      </div>
    </div>
  );
};


const SidebarSponsored = () => {
  const [ad, setAd] = useState(null);
  useEffect(() => {
    api.get('/sponsored-ads/public', { params: { placement: 'blog_card', limit: 1 } })
      .then(r => setAd((r.data || [])[0] || null))
      .catch(() => {});
  }, []);
  if (!ad) return null;
  return <SponsoredAd ad={ad} />;
};


export default BlogPost;
